import { Injectable } from "@angular/core";
import { WeaponType, DefenceType, Comparator, Rarity, VendoringGearType } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { AdvancedConditions, CustomRule, DisplayOptions, Filter } from "../../models/filter-models";
import { GenerationHelperService } from "./generation-helper.service";
import { ItemDataService } from "../item-data.service";
import { RuleGeneratorService } from "./rule-generator.service";
import { poe1, poe2 } from "../../../game";
import { RuleId } from "../../models/rule-id";
import { PresetsService } from "../presets.service";
import { ClassType } from "../../data/poe2/classes";

type DefencePatternValue = 0 | 1 | 2; // 0 = must not have, 1 = must have, 2 = don't care
type DefencePattern = [DefencePatternValue, DefencePatternValue, DefencePatternValue]; // [Armour, Evasion, EnergyShield]

export interface DefenceRule {
  minArmour?: number;
  minEvasion?: number;
  minEnergyShield?: number;
}

const DEFENCE_TYPE_MASK: Record<DefenceType, number | undefined> = {
  [DefenceType.Armour]: 0b100,
  [DefenceType.Evasion]: 0b010,
  [DefenceType.EnergyShield]: 0b001,
  [DefenceType.ArmourEvasion]: 0b110,
  [DefenceType.ArmourEnergyShield]: 0b101,
  [DefenceType.EvasionEnergyShield]: 0b011,
  [DefenceType.All]: undefined, // handled specially
};

const DEFENCE_MASKS = Object.values(DEFENCE_TYPE_MASK).filter((mask): mask is number => mask !== undefined);

function maskMatchesPattern(pattern: DefencePattern, mask: number): boolean {
  const bits: [number, number, number] = [(mask >> 2) & 1, (mask >> 1) & 1, mask & 1];
  return pattern.every((p, idx) => p === 2 || p === bits[idx]);
}

function patternToRule([armour, evasion, es]: DefencePattern): DefenceRule {
  const convert = (v: DefencePatternValue) => v === 2 ? undefined : v === 1 ? 1 : 0;
  return {
    minArmour: convert(armour),
    minEvasion: convert(evasion),
    minEnergyShield: convert(es),
  };
}

export const STARTER_WEAPON_BASE_TYPES: { class: ClassType, baseTypes: string[] }[] = [
  { class: 'Warrior', baseTypes: ['Wooden Club'] },
  { class: 'Ranger', baseTypes: ['Crude Bow'] },
  { class: 'Huntress', baseTypes: ['Hardwood Spear', 'Leather Buckler'] },
  { class: 'Witch', baseTypes: ['Withered Wand', 'Rattling Sceptre'] },
  { class: 'Sorceress', baseTypes: ['Withered Wand', 'Voltaic Staff'] },
  { class: 'Mercenary', baseTypes: ['Makeshift Crossbow'] },
  { class: 'Monk', baseTypes: ['Wrapped Quarterstaff'] },
  { class: 'Druid', baseTypes: ['Changeling Talisman', 'Ashen Staff'] },
];

export function buildDefenceRules(defenceTypes: DefenceType[]): DefenceRule[] {
  const allowed = new Set<number>();

  if (defenceTypes.includes(DefenceType.All)) {
    DEFENCE_MASKS.forEach(mask => allowed.add(mask));
  } else {
    defenceTypes.forEach(type => {
      const mask = DEFENCE_TYPE_MASK[type];
      if (mask !== undefined) {
        allowed.add(mask);
      }
    });
  }

  // If nothing is selected, default to a permissive rule (don't filter by defences).
  if (!allowed.size) {
    return [{ minArmour: undefined, minEvasion: undefined, minEnergyShield: undefined }];
  }

  const patterns: { pattern: DefencePattern; covers: number[] }[] = [];
  const values: DefencePatternValue[] = [0, 1, 2];

  for (const armour of values) {
    for (const evasion of values) {
      for (const es of values) {
        const pattern: DefencePattern = [armour, evasion, es];
        const coverage = DEFENCE_MASKS.filter(mask => maskMatchesPattern(pattern, mask));
        if (!coverage.length) continue;

        const isValid = coverage.every(mask => allowed.has(mask));
        if (!isValid) continue;

        const allowedCoverage = coverage.filter(mask => allowed.has(mask));
        if (allowedCoverage.length) {
          patterns.push({ pattern, covers: allowedCoverage });
        }
      }
    }
  }

  patterns.sort((a, b) => b.covers.length - a.covers.length);

  let best: DefencePattern[] | null = null;
  const allowedSize = allowed.size;

  function backtrack(start: number, chosen: DefencePattern[], covered: Set<number>) {
    if (best && chosen.length >= best.length) return;
    if (covered.size === allowedSize) {
      best = chosen.slice();
      return;
    }

    for (let i = start; i < patterns.length; i++) {
      const { pattern, covers } = patterns[i];
      const newOnes = covers.filter(mask => !covered.has(mask));
      if (!newOnes.length) continue;

      newOnes.forEach(mask => covered.add(mask));
      chosen.push(pattern);
      backtrack(i + 1, chosen, covered);
      chosen.pop();
      newOnes.forEach(mask => covered.delete(mask));
    }
  }

  backtrack(0, [], new Set<number>());

  const chosenPatterns = best ?? [];
  return chosenPatterns.map(patternToRule);
}

@Injectable({
  providedIn: 'root'
})

export class LevelingGearGeneratorService {
  oneHandWeaponTypes: WeaponType[];

  constructor(private itemDataService: ItemDataService, private helper: GenerationHelperService, private ruleFormatter: RuleGeneratorService) {
    this.oneHandWeaponTypes = this.itemDataService.getOneHandWeaponTypes();
  }

  private splitWeaponClassesBySpecialTypes(weaponClasses: string[], ignoreLevelDifference: boolean): { special: string[], nonSpecial: string[] } {
    if (!ignoreLevelDifference) {
      return { special: [], nonSpecial: weaponClasses };
    }

    const specialWeaponTypes = PresetsService.specialWeaponTypes.map(wt => wt.toString());
    const special: string[] = [];
    const nonSpecial: string[] = [];

    weaponClasses.forEach(wc => {
      if (specialWeaponTypes.includes(wc)) {
        special.push(wc);
      } else {
        nonSpecial.push(wc);
      }
    });

    return { special, nonSpecial };
  }

  public generateLevelingGearExceptionsRules(filter: Filter): string {
    if (filter.showLevelingGear === false) return '';

    const minAreaLevel = filter.showLevelingGearMinAreaLevel ?? 0;
    const maxAreaLevel = filter.showLevelingGearMaxAreaLevel ?? 80;
    if (maxAreaLevel <= minAreaLevel) {
      return '';
    }

    let weaponRules = '';
    let is2hWeaponOnly = !filter.showLevelingGearWeaponClasses.includes('All') && !filter.showLevelingGearWeaponClasses.some(c => this.oneHandWeaponTypes.includes(c as WeaponType));

    if (filter.showLevelingGearWeapons) {
      if (poe2) {
        weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          baseTypes: [...new Set(STARTER_WEAPON_BASE_TYPES.flatMap(c => c.baseTypes))],
          advancedConditions: new AdvancedConditions({
            minAreaLevel: 1,
            maxAreaLevel: 1,
          }),
        }), filter, 'Starter Weapons on The Riverbank', undefined, 'gear-leveling-weapons-normal') + '\n\n';
      }

      const weaponClasses = filter.showLevelingGearWeaponClasses.includes('All')
        ? this.itemDataService.getAllWeaponTypes()
        : filter.showLevelingGearWeaponClasses as string[];
      const weaponClassesWithoutQuivers = filter.showLevelingGearWeaponClasses.includes('All')
        ? this.itemDataService.getAllWeaponTypes().filter(c => c !== 'Quivers')
        : filter.showLevelingGearWeaponClasses.filter(c => c !== 'Quivers') as string[];

      // Generate Excellent Socketed rules
      if (poe1 && filter.showLevelingGearExcellentSocketed && filter.showLevelingGearExcellentSocketGroup &&
          (filter.showLevelingGearWeaponClasses.length > 1 || !filter.showLevelingGearWeaponClasses.includes('Quivers'))) {
        const baseSocketGroup = filter.showLevelingGearExcellentSocketGroup;
        const baseLinkCount = baseSocketGroup.length;

        // Loop through link counts from base to 6
        for (let currentLinkCount = baseLinkCount; currentLinkCount <= 6; currentLinkCount++) {
          // Build socket group by adding 'X' for each additional link
          const additionalLinks = currentLinkCount - baseLinkCount;
          const socketGroup = baseSocketGroup + 'X'.repeat(additionalLinks);

          // First appearance rules
          const firstAppearanceRanges = this.getFirstAppearanceRanges(currentLinkCount);
          if (firstAppearanceRanges && firstAppearanceRanges.maxAreaLevel >= minAreaLevel) {
            weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingExcellentSocketedFirstAppearance, new CustomRule({
              filterItemClass: weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({
                minAreaLevel: firstAppearanceRanges.minAreaLevel,
                maxAreaLevel: firstAppearanceRanges.maxAreaLevel,
                socketGroup: socketGroup
              }),
            }), filter, 'Excellent Socketed Leveling Weapons (First Appearance)') + '\n\n';
          }

          // Regular excellent socketed rules
          if (filter.showLevelingGearExcellentSocketedDifference !== null) {
            let firstRule = true;
            for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
              if (areaLevel < minAreaLevel) { firstRule = false; continue; }
              const dropLevel = areaLevel - filter.showLevelingGearExcellentSocketedDifference;
              if (dropLevel < 0) continue;

              const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
              firstRule = false;

              weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingExcellentSocketed, new CustomRule({
                filterItemClass: weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' '),
                advancedConditions: new AdvancedConditions({
                  maxAreaLevel: areaLevel,
                  minDropLevel: effectiveMinDropLevel,
                  socketGroup: socketGroup
                }),
              }), filter, 'Excellent Socketed Leveling Weapons') + '\n\n';
            }
          } else {
            // Always show (no level difference)
            weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingExcellentSocketed, new CustomRule({
              filterItemClass: weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({
                socketGroup: socketGroup
              }),
            }), filter, 'Leveling Weapons Excellent Socketed') + '\n\n';
          }
        }
      }

      // Generate Good Socketed rules
      if (poe1 && filter.showLevelingGearOtherSocketed && filter.showLevelingGearOtherSocketGroup &&
          (filter.showLevelingGearWeaponClasses.length > 1 || !filter.showLevelingGearWeaponClasses.includes('Quivers'))) {
        const socketGroup = filter.showLevelingGearOtherSocketGroup;
        const linkCount = socketGroup.length;

        // First appearance rules
        const firstAppearanceRanges = this.getFirstAppearanceRanges(linkCount);
        if (firstAppearanceRanges && firstAppearanceRanges.maxAreaLevel >= minAreaLevel) {
          weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingGoodSocketedFirstAppearance, new CustomRule({
            filterItemClass: weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({
              minAreaLevel: firstAppearanceRanges.minAreaLevel,
              maxAreaLevel: firstAppearanceRanges.maxAreaLevel,
              socketGroup: socketGroup
            }),
          }), filter, 'Good Socketed Leveling Weapons (First Appearance)') + '\n\n';
        }

        // Regular good socketed rules
        if (filter.showLevelingGearOtherSocketedLevelDifference !== null) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearOtherSocketedLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingGoodSocketed, new CustomRule({
              filterItemClass: weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({
                maxAreaLevel: areaLevel,
                minDropLevel: effectiveMinDropLevel,
                socketGroup: socketGroup
              }),
            }), filter, 'Good Socketed Leveling Weapons') + '\n\n';
          }
        } else {
          // Always show (no level difference)
          weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingGoodSocketed, new CustomRule({
            filterItemClass: weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({
              socketGroup: socketGroup
            }),
          }), filter, 'Leveling Weapons Good Socketed') + '\n\n';
        }
      }

      // Generate Rare rules
      if (filter.showLevelingGearRare) {
        const { special: specialWeaponClasses, nonSpecial: nonSpecialWeaponClasses } = this.splitWeaponClassesBySpecialTypes(
          weaponClasses,
          (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes) ?? false
        );

        // Generate rules for special weapon types (always show, no level difference) - POE2 only
        if (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes && specialWeaponClasses.length > 0) {
          const rareEquipmentType = (filter.showVendoringRares && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingRaresStyle
            : EquipmentType.OtherGearLevelingRare;
          weaponRules += this.ruleFormatter.generateEquipmentRule(rareEquipmentType, new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Rare,
            filterItemClass: specialWeaponClasses.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({}),
          }), filter, 'Rare Special Leveling Weapons') + '\n\n';
        }

        // Generate rules for non-special weapon types (with level difference)
        if (nonSpecialWeaponClasses.length > 0) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearRareLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            const rareEquipmentType = (filter.showVendoringRares && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
              ? EquipmentType.OtherGearLevelingRaresStyle
              : EquipmentType.OtherGearLevelingRare;
            weaponRules += this.ruleFormatter.generateEquipmentRule(rareEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Rare,
              filterItemClass: nonSpecialWeaponClasses.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({ maxAreaLevel: areaLevel, minDropLevel: effectiveMinDropLevel }),
            }), filter, 'Rare Leveling Weapons') + '\n\n';
          }
        }
      }

      // Generate Magic rules
      if (filter.showLevelingGearMagic) {
        const { special: specialWeaponClasses, nonSpecial: nonSpecialWeaponClasses } = this.splitWeaponClassesBySpecialTypes(
          weaponClasses,
          (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes) ?? false
        );

        // Generate rules for special weapon types (always show, no level difference) - POE2 only
        if (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes && specialWeaponClasses.length > 0) {
          const magicEquipmentType = (filter.showVendoringMagics && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingMagicsStyle
            : EquipmentType.OtherGearLevelingMagic;
          weaponRules += this.ruleFormatter.generateEquipmentRule(magicEquipmentType, new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Magic,
            filterItemClass: specialWeaponClasses.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({}),
          }), filter, 'Magic Special Leveling Weapons') + '\n\n';
        }

        // Generate rules for non-special weapon types (with level difference)
        if (nonSpecialWeaponClasses.length > 0) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearMagicLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            const magicEquipmentType = (filter.showVendoringMagics && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
              ? EquipmentType.OtherGearLevelingMagicsStyle
              : EquipmentType.OtherGearLevelingMagic;
            weaponRules += this.ruleFormatter.generateEquipmentRule(magicEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Magic,
              filterItemClass: nonSpecialWeaponClasses.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({ maxAreaLevel: areaLevel, minDropLevel: effectiveMinDropLevel }),
            }), filter, 'Magic Leveling Weapons') + '\n\n';
          }
        }
      }

      // Generate Normal rules
      if (filter.showLevelingGearNormal) {
        const { special: specialWeaponClasses, nonSpecial: nonSpecialWeaponClasses } = this.splitWeaponClassesBySpecialTypes(
          weaponClasses,
          (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes) ?? false
        );

        // Generate rules for special weapon types (always show, no level difference) - POE2 only
        if (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes && specialWeaponClasses.length > 0) {
          weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            filterItemClass: specialWeaponClasses.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({}),
          }), filter, 'Normal Special Leveling Weapons') + '\n\n';
        }

        // Generate rules for non-special weapon types (with level difference)
        if (nonSpecialWeaponClasses.length > 0) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearNormalLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              filterItemClass: nonSpecialWeaponClasses.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({ maxAreaLevel: areaLevel, minDropLevel: effectiveMinDropLevel }),
            }), filter, 'Normal Leveling Weapons') + '\n\n';
          }
        }
      }
    }

    let armourRules = '';

    if (filter.showLevelingGearAllMagicRareBoots) {
      const bootsMaxArea = filter.showLevelingGearAllMagicRareBootsMaxAreaLevel;
      const cappedBootsMaxArea = Math.min(bootsMaxArea, maxAreaLevel);
      armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingMagicRareBoots, new CustomRule({
        itemClass: 'Boots',
        advancedConditions: new AdvancedConditions({
          maxAreaLevel: cappedBootsMaxArea,
        })
      }), filter, 'Magic & Rare Leveling Boots', { customLine: '\n  Rarity >= Magic\n  Rarity <= Rare' }, 'gear-leveling-armour-magic-rare-boots') + '\n\n';
    }

    if (filter.showLevelingGearArmour) {
      const classList = is2hWeaponOnly ? ['Body Armours', 'Boots', 'Gloves', 'Helmets'] : ['Body Armours', 'Boots', 'Gloves', 'Helmets', 'Shields'];

      if (poe2 && filter.showLevelingGearArmourDefenceTypes.includes(DefenceType.Evasion)
        && filter.showLevelingGearWeaponClasses.includes(WeaponType.Spears)) {
        classList.push('Bucklers');
      }

      // Build defence exclusions via advanced conditions
      const defenceTypes = filter.showLevelingGearArmourDefenceTypes as DefenceType[];
      const defenceRules = buildDefenceRules(defenceTypes);

      const defenceAdvancedConditions = (defenceRule: DefenceRule, areaLevel?: number, dropLevel?: number, socketGroup?: string, minAreaLevelOverride?: number, maxAreaLevelOverride?: number) => new AdvancedConditions({
        minAreaLevel: minAreaLevelOverride,
        maxAreaLevel: maxAreaLevelOverride ?? areaLevel,
        minDropLevel: dropLevel,
        socketGroup,
        minArmour: defenceRule.minArmour,
        minEnergyShield: defenceRule.minEnergyShield,
        minEvasion: defenceRule.minEvasion,
      });

      // Generate Excellent Socketed rules
      if (poe1 && filter.showLevelingGearExcellentSocketed && filter.showLevelingGearExcellentSocketGroup) {
        const baseSocketGroup = filter.showLevelingGearExcellentSocketGroup;
        const baseLinkCount = baseSocketGroup.length;

        // Loop through link counts from base to 6
        for (let currentLinkCount = baseLinkCount; currentLinkCount <= 6; currentLinkCount++) {
          // Build socket group by adding 'X' for each additional link
          const additionalLinks = currentLinkCount - baseLinkCount;
          const socketGroup = baseSocketGroup + 'X'.repeat(additionalLinks);

          // First appearance rules
          const firstAppearanceRanges = this.getFirstAppearanceRanges(currentLinkCount);
          if (firstAppearanceRanges) {
            for (const defenceRule of defenceRules) {
              armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingExcellentSocketedFirstAppearance, new CustomRule({
                filterItemClass: classList.map(c => `"${c}"`).join(' '),
                advancedConditions: defenceAdvancedConditions(
                  defenceRule,
                  undefined,
                  undefined,
                  socketGroup,
                  firstAppearanceRanges.minAreaLevel,
                  firstAppearanceRanges.maxAreaLevel
                ),
              }), filter, 'Excellent Socketed Leveling Armour (First Appearance)', undefined, 'gear-leveling-armour-excellent-socketed-first' as RuleId) + '\n\n';
            }
          }

          // Regular excellent socketed rules
          if (filter.showLevelingGearExcellentSocketedDifference !== null) {
            let firstRule = true;
            for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
              if (areaLevel < minAreaLevel) { firstRule = false; continue; }
              const dropLevel = areaLevel - filter.showLevelingGearExcellentSocketedDifference;
              if (dropLevel < 0) continue;

              const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
              firstRule = false;

              for (const defenceRule of defenceRules) {
                armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingExcellentSocketed, new CustomRule({
                  filterItemClass: classList.map(c => `"${c}"`).join(' '),
                  advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel, socketGroup),
                }), filter, 'Excellent Socketed Leveling Armour', undefined, 'gear-leveling-armour-excellent-socketed' as RuleId) + '\n\n';
              }
            }
          } else {
            // Always show (no level difference)
            for (const defenceRule of defenceRules) {
              armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingExcellentSocketed, new CustomRule({
                filterItemClass: classList.map(c => `"${c}"`).join(' '),
                advancedConditions: defenceAdvancedConditions(
                  defenceRule,
                  undefined,
                  undefined,
                  socketGroup
                ),
              }), filter, 'Leveling Armour Excellent Socketed', undefined, 'gear-leveling-armour-excellent-socketed' as RuleId) + '\n\n';
            }
          }
        }
      }

      // Generate Good Socketed rules
      if (poe1 && filter.showLevelingGearOtherSocketed && filter.showLevelingGearOtherSocketGroup) {
        const socketGroup = filter.showLevelingGearOtherSocketGroup;
        const linkCount = socketGroup.length;

        // First appearance rules
        const firstAppearanceRanges = this.getFirstAppearanceRanges(linkCount);
        if (firstAppearanceRanges) {
          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingGoodSocketedFirstAppearance, new CustomRule({
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(
                defenceRule,
                undefined,
                undefined,
                socketGroup,
                firstAppearanceRanges.minAreaLevel,
                firstAppearanceRanges.maxAreaLevel
              ),
            }), filter, 'Good Socketed Leveling Armour (First Appearance)', undefined, 'gear-leveling-armour-good-socketed-first' as RuleId) + '\n\n';
          }
        }

        // Regular good socketed rules
        if (filter.showLevelingGearOtherSocketedLevelDifference !== null) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearOtherSocketedLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            for (const defenceRule of defenceRules) {
              armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingGoodSocketed, new CustomRule({
                filterItemClass: classList.map(c => `"${c}"`).join(' '),
                advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel, socketGroup),
              }), filter, 'Good Socketed Leveling Armour', undefined, 'gear-leveling-armour-good-socketed' as RuleId) + '\n\n';
            }
          }
        } else {
          // Always show (no level difference)
          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingGoodSocketed, new CustomRule({
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(
                defenceRule,
                undefined,
                undefined,
                socketGroup
              ),
            }), filter, 'Leveling Armour Good Socketed', undefined, 'gear-leveling-armour-good-socketed' as RuleId) + '\n\n';
          }
        }
      }

      // Generate Rare rules
      if (filter.showLevelingGearRare) {
        let firstRule = true;
        for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
          if (areaLevel < minAreaLevel) { firstRule = false; continue; }
          const dropLevel = areaLevel - filter.showLevelingGearRareLevelDifference;
          if (dropLevel < 0) continue;

          const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
          firstRule = false;

          const rareArmourEquipmentType = (filter.showVendoringRares && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingRaresStyle
            : EquipmentType.OtherGearLevelingRare;
          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(rareArmourEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Rare,
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel),
            }), filter, 'Rare Leveling Armour', undefined, 'gear-leveling-armour-rare' as RuleId) + '\n\n';
          }
        }
      }

      // Generate Magic rules
      if (filter.showLevelingGearMagic) {
        let firstRule = true;
        for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
          if (areaLevel < minAreaLevel) { firstRule = false; continue; }
          const dropLevel = areaLevel - filter.showLevelingGearMagicLevelDifference;
          if (dropLevel < 0) continue;

          const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
          firstRule = false;

          const magicArmourEquipmentType = (filter.showVendoringMagics && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingMagicsStyle
            : EquipmentType.OtherGearLevelingMagic;
          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(magicArmourEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Magic,
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel),
            }), filter, 'Magic Leveling Armour', undefined, 'gear-leveling-armour-magic' as RuleId) + '\n\n';
          }
        }
      }

      // Generate Normal rules
      if (filter.showLevelingGearNormal) {
        let firstRule = true;
        for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
          if (areaLevel < minAreaLevel) { firstRule = false; continue; }
          const dropLevel = areaLevel - filter.showLevelingGearNormalLevelDifference;
          if (dropLevel < 0) continue;

          const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
          firstRule = false;

          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel),
            }), filter, 'Normal Leveling Armour', undefined, 'gear-leveling-armour-normal' as RuleId) + '\n\n';
          }
        }
      }
    }

    // Special rule for rares in first 5 areas
    if (filter.showLevelingGear) {
      armourRules += this.ruleFormatter.generateRule(new CustomRule({
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Rare,
        displayOptions: new DisplayOptions(),
        advancedConditions: new AdvancedConditions({ maxAreaLevel: 5 })
      }), filter, 'gear-leveling-rare-first-areas', 'Leveling Rare Gear (First Areas)') + '\n\n';
    }

    return weaponRules + '\n\n' + armourRules;
  }

  private getFirstAppearanceRanges(linkCount: number): { minAreaLevel: number; maxAreaLevel: number } | null {
    // Lower link numbers can drop always (no maxAreaLevel restriction)
    if (linkCount < 4) {
      return null;
    }

    switch (linkCount) {
      case 4:
        return { minAreaLevel: 25, maxAreaLevel: 35 };
      case 5:
        return { minAreaLevel: 35, maxAreaLevel: 50 };
      case 6:
        return { minAreaLevel: 50, maxAreaLevel: 65 };
      default:
        return null;
    }
  }
}
